<template>
  <AddressTableResultItem
    v-if="address"
    :address="address"
    :is-removable="isRemovable"
    :is-disabled="isDisabled"
    @on-remove="emit('update:address', undefined)"
  />
  <GoogleMapsAutocompleteInput
    v-else
    :address-validation="addressValidation"
    :use-legacy-design="useLegacyDesign"
    :placeholder="placeholder"
    @on-place-changed="handlePlaceChanged"
  />
</template>

<script setup lang="ts">
import { useTalentQuery } from "~/components/talent/composables/useTalentQuery";
import { useTalentUpdate } from "~/components/talent/composables/useTalentUpdate";
import { useSupabase } from "~/composables/useSupabase";
import { useToast } from "~/composables/useToast";
import { baseAddressTableColumns } from "@asap/shared/utils/supabase.base-columns";
import type { BaseAddressTable } from "@asap/shared/utils/supabase.types";
import type { Database } from "@asap/shared/types/supabase.gen";
import type { UpdateTalentAddressBody } from "@asap/shared/schemas/talent/talent";
import { convertToAddressTableInsert } from "@asap/shared/lib/google-maps";
import { useActorFields } from "~/composables/useActorFields";

type AddressTableInsert = Database["public"]["Tables"]["address_table"]["Insert"];

type Props = {
  isDisabled?: boolean;
  isRemovable?: boolean;
  address?: AddressTableInsert;
  addressValidation?: (addressComponents: google.maps.GeocoderAddressComponent[]) => void;
  /**
   * Set this when the address being edited is a talent's. The API then owns the write, so the change
   * publishes its domain event. Left unset (missions, companies, amendments), the row is written
   * directly as before.
   *
   * Note this changes *when* the address is stored: the endpoint writes and links it as soon as a
   * place is picked, rather than the parent form persisting it on save. A consultant who picks an
   * address and then abandons the form has still changed the talent's address.
   */
  talentId?: string;
  useLegacyDesign?: boolean;
  placeholder?: string;
};

type Emits = {
  "update:address": [address?: BaseAddressTable];
};

const props = withDefaults(defineProps<Props>(), {
  isDisabled: false,
  isRemovable: true,
  address: undefined,
  addressValidation: undefined,
  talentId: undefined,
  useLegacyDesign: true,
  placeholder: undefined,
});

const emit = defineEmits<Emits>();

const supabase = useSupabase();
const { actorFields } = useActorFields();
const { update: updateTalent } = useTalentUpdate();
const { selectAddress } = useTalentQuery();
const { addToastError, addToastWarning } = useToast();
const { t } = useI18n();

/**
 * The write is reported: this runs from a `place_changed` listener, so a rejected promise here has
 * nowhere to surface — the app installs no global Vue error handler, and the widget has already put
 * the picked text in the field. Without this the consultant sees a filled-looking input, nothing
 * stored, and no clue why.
 */
const handlePlaceChanged = async function (place: google.maps.places.PlaceResult) {
  const talentId = props.talentId;

  try {
    if (talentId) await persistTalentAddress(talentId, place);
    else await upsertAddressRow(place);
  } catch (error) {
    addToastError({ title: t("common.addressTableInput.saveError") }, error);
  }
};

const upsertAddressRow = async function (place: google.maps.places.PlaceResult) {
  const address = convertToAddressTableInsert(place);

  const { created_by, updated_by, updated_at } = actorFields();
  const addressUpsertWithActor: AddressTableInsert = props.address?.id
    ? { id: props.address.id, ...address, updated_by, updated_at }
    : { ...address, created_by };

  const { data, error } = await supabase
    .from("address_table")
    .upsert(addressUpsertWithActor)
    .select(baseAddressTableColumns)
    .single();

  if (error) throw error;

  emit("update:address", data);
};

/**
 * The place id is sent rather than the mapped columns: the endpoint accepts only what a client can
 * legitimately know and re-resolves the address itself. The stored row is then read back, because the
 * write answers with a status and callers here bind to the address itself.
 */
const persistTalentAddress = async function (talentId: string, place: google.maps.places.PlaceResult) {
  const address = toAddressBody(place);

  // Nothing worth a Sentry entry — the consultant typed instead of picking, so say so and stop.
  if (!address) {
    addToastWarning({
      title: t("common.addressTableInput.incompleteAddressTitle"),
      description: t("common.addressTableInput.incompleteAddressDescription"),
    });
    return;
  }

  await updateTalent(talentId, { address });

  const { data } = await selectAddress(talentId);

  emit("update:address", data ?? undefined);
};

const toAddressBody = function (place: google.maps.places.PlaceResult): UpdateTalentAddressBody | null {
  if (place.place_id) return { place_id: place.place_id };
  if (place.formatted_address) return { formatted: place.formatted_address };

  // The widget fires `place_changed` with a name-only result when free text is submitted without
  // taking a prediction. The endpoint treats an empty address as no change, so sending it would be
  // harmless but silent — this exists to tell the consultant why nothing happened.
  return null;
};
</script>

<style scoped></style>
